import { Stack } from "@mui/material";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import {
  type ExternalAccountLite,
  type PayoutSchedule,
} from "@src/appV2/redesign/Accounts/PayoutSettings/api/schema";
import { ExternalAccountLogo } from "@src/appV2/redesign/Accounts/PayoutSettings/components/ExternalAccountLogo";
import { Button } from "@src/appV2/redesign/components/Button";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import {
  CbhIcon,
  Divider,
  Illustration,
  LoadingSpinner,
  Text,
  Title,
  useModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/utils/getSafeAreaPaddings";
import Confetti from "react-confetti";

import { HowGettingPaidWorksBottomSheet } from "./HowGettingPaidWorksBottomSheet";

export interface StripeOnboardingCompletePageContentProps {
  payoutSchedule: PayoutSchedule;
  payoutFee: number;
  destination: ExternalAccountLite;
  isStartBookingLoading: boolean;
  onStartBooking: () => void;
}

export function StripeOnboardingCompletePageContent(
  props: StripeOnboardingCompletePageContentProps
) {
  const { payoutSchedule, payoutFee, destination, isStartBookingLoading, onStartBooking } = props;
  const howGettingPaidWorksModalState = useModalState();

  return (
    <Stack
      sx={(theme) => ({
        flex: 1,
        justifyContent: "space-between",
        paddingX: theme.spacing(5),
        paddingTop: theme.spacing(10),
        ...getSafeAreaPaddings({ bottom: true, fallbackBottom: theme.spacing(6) }),
      })}
    >
      <Confetti
        recycle={false}
        width={window.innerWidth}
        height={window.innerHeight}
        numberOfPieces={200}
        gravity={0.4}
      />
      <Stack>
        <Stack spacing={8} sx={{ alignItems: "center" }}>
          <Illustration type="shift-booked" />
          <Stack spacing={5}>
            <Title variant="h3" component="h3" sx={{ textAlign: "center" }}>
              You&apos;re all set to get paid!
            </Title>
            <Text
              variant="body2"
              sx={(theme) => ({
                textAlign: "center",
                marginTop: theme.spacing(3),
                color: theme.palette.text.secondary,
              })}
            >
              Your Stripe account is now fully configured.
            </Text>
          </Stack>
        </Stack>

        <Divider sx={(theme) => ({ marginY: theme.spacing(8) })} />

        <Stack spacing={6}>
          <Stack
            direction="row"
            sx={(theme) => ({
              justifyContent: "space-between",
              alignItems: "center",
              height: theme.spacing(6),
            })}
          >
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Payout method
            </Text>
            <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
              {payoutSchedule === "instant" && <CbhIcon type="bolt-filled" size="xSmall" />}
              <Text semibold variant="body2">
                {payoutSchedule === "instant" ? "InstantPay" : "Standard Payout"}
              </Text>
            </Stack>
          </Stack>

          <Stack
            direction="row"
            sx={(theme) => ({
              justifyContent: "space-between",
              alignItems: "center",
              height: theme.spacing(6),
            })}
          >
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Fee per payout
            </Text>
            <Text semibold variant="body2">
              {formatDollarsAsUsd(payoutFee)}
            </Text>
          </Stack>

          <Stack
            direction="row"
            sx={(theme) => ({
              justifyContent: "space-between",
              alignItems: "center",
              height: theme.spacing(6),
            })}
          >
            <Text variant="body2" sx={(theme) => ({ color: theme.palette.text.secondary })}>
              Destination
            </Text>
            <Stack direction="row" sx={{ alignItems: "center" }} spacing={1}>
              <ExternalAccountLogo externalAccount={destination} iconSize="medium" />
              <Text semibold variant="body2">
                {destination.providerName} ···· {destination.last4}
              </Text>
            </Stack>
          </Stack>
        </Stack>
      </Stack>

      <Stack sx={{ alignItems: "center" }} spacing={4}>
        <Button fullWidth disabled={isStartBookingLoading} onClick={onStartBooking}>
          {isStartBookingLoading ? <LoadingSpinner size="small" /> : "Start booking"}
        </Button>
        <TextButton onClick={howGettingPaidWorksModalState.openModal}>
          How getting paid works
        </TextButton>
      </Stack>
      <HowGettingPaidWorksBottomSheet modalState={howGettingPaidWorksModalState} />
    </Stack>
  );
}
